/**
 * iMin Printer Service
 *
 * One receipt layout, three transports, in this order:
 *   1. HostPoint Android wrapper bridge (window.HostPointPrinter) — native iMin
 *      PrinterSDK when present, Bluetooth SPP when not. Works offline.
 *   2. iMin JavaScript SDK in Chrome (window.IminPrintInstance / window.imin).
 *   3. Browser print window (handled by the caller as a fallback).
 *
 * Connection type: M2-202 / M2-203 / M2 Pro use SPI, every other supported
 * model (D4, D4 Pro, Falcon, Swift, M2 Max) uses USB. The first init attempt
 * follows the model; if that fails we try the other type once and remember
 * whichever worked.
 */

const CONNECT_TYPE_KEY = 'imin_connect_type';
const SPI_MODELS = /m2[-\s_]?(202|203)|m2\s*pro/i;

// iMin PrintConnectType values (V1 SDK and JS SDK agree: USB=0, SPI=1, BT=2)
export const CONNECT = { USB: 0, SPI: 1, BLUETOOTH: 2 };

const win = () => (typeof window !== 'undefined' ? window : {});

export const detectIminModel = () => {
  const ua = (navigator?.userAgent || '').toLowerCase();
  const models = [
    ['d4 pro', 'D4 Pro', CONNECT.USB, 80],
    ['d4', 'D4', CONNECT.USB, 80],
    ['swift 2', 'Swift 2', CONNECT.USB, 80],
    ['swift', 'Swift 1', CONNECT.USB, 80],
    ['falcon 2', 'Falcon 2', CONNECT.USB, 80],
    ['falcon', 'Falcon 1', CONNECT.USB, 80],
    ['m2 max', 'M2 Max', CONNECT.USB, 58],
    ['m2 pro', 'M2 Pro', CONNECT.SPI, 58],
    ['m2-203', 'M2-203', CONNECT.SPI, 58],
    ['m2-202', 'M2-202', CONNECT.SPI, 58],
    ['swan', 'Swan', CONNECT.USB, 80],
  ];
  const hit = models.find(([pattern]) => ua.includes(pattern));
  return hit
    ? { name: hit[1], connectType: hit[2], paperWidth: hit[3] }
    : { name: '', connectType: SPI_MODELS.test(ua) ? CONNECT.SPI : CONNECT.USB, paperWidth: 80 };
};

const preferredConnectType = () => {
  const model = detectIminModel();
  const [savedModel, savedType] = (localStorage.getItem(CONNECT_TYPE_KEY) || '').split(':');
  const parsed = Number(savedType);
  if (savedModel === (model.name || 'unknown') && (parsed === CONNECT.USB || parsed === CONNECT.SPI)) {
    return parsed;
  }
  return model.connectType;
};

const rememberConnectType = (connectType) => {
  localStorage.setItem(CONNECT_TYPE_KEY, `${detectIminModel().name || 'unknown'}:${connectType}`);
};

// ─── Transport: HostPoint Android wrapper bridge ──────────────────────
const bridgeBackend = () => {
  const bridge = win().HostPointPrinter;
  if (!bridge) return null;
  try {
    if (typeof bridge.isAvailable === 'function' && !bridge.isAvailable()) return null;
  } catch {
    return null;
  }

  const call = (name, ...args) => {
    if (typeof bridge[name] !== 'function') return false;
    try {
      return bridge[name](...args);
    } catch (error) {
      console.warn(`[iMin bridge] ${name} failed`, error);
      return false;
    }
  };

  return {
    kind: 'bridge',
    init: (connectType) => call('initPrinter', connectType) !== false,
    setAlignment: (align) => call('setAlignment', align),
    setTextSize: (size) => call('setTextSize', size),
    setTextStyle: (style) => call('setTextStyle', style),
    text: (value) => call('printText', value, '{}'),
    columns: (cols, widths, aligns, sizes) =>
      call(
        'printColumnsText',
        JSON.stringify(cols.map(String)),
        JSON.stringify(widths),
        JSON.stringify(aligns),
        JSON.stringify(sizes)
      ),
    qr: (data, size) => call('printQrCode', data, size, 1),
    bitmap: (source) => call('printBitmap', source, 0),
    feed: (dots) => call('feedPaper', Math.max(3, Math.round(dots / 24))),
    lineFeed: () => call('printText', '\n', '{}'),
    cut: () => call('cutPaper'),
    openDrawer: () => call('openCashBox') !== false,
    test: () => call('testPrint') !== false,
    status: async () => {
      const raw = call('getPrinterStatus');
      return typeof raw === 'number' ? raw : -1;
    },
    info: () => {
      try {
        return JSON.parse(bridge.getDeviceInfo?.() || '{}');
      } catch {
        return {};
      }
    },
  };
};

// ─── Transport: iMin JavaScript SDK (Chrome / installed PWA) ──────────
const jsSdkBackend = () => {
  const sdk = win().IminPrintInstance || win().imin;
  if (!sdk || typeof sdk.printText !== 'function') return null;

  const safe = (name, ...args) => {
    if (typeof sdk[name] !== 'function') return false;
    try {
      sdk[name](...args);
      return true;
    } catch (error) {
      console.warn(`[iMin JS SDK] ${name} failed`, error);
      return false;
    }
  };

  const TYPE_NAME = { [CONNECT.USB]: 'USB', [CONNECT.SPI]: 'SPI', [CONNECT.BLUETOOTH]: 'Bluetooth' };
  const sdkType = (connectType) => {
    const name = TYPE_NAME[connectType] || 'USB';
    return sdk.PrintConnectType?.[name] ?? name;
  };

  return {
    kind: 'jssdk',
    init: (connectType) => safe('initPrinter', sdkType(connectType)),
    setAlignment: (align) => safe('setAlignment', align),
    setTextSize: (size) => safe('setTextSize', size),
    setTextStyle: (style) => safe('setTextStyle', style),
    text: (value) => safe('printText', value),
    columns: (cols, widths, aligns, sizes, width) =>
      safe('printColumnsText', cols.map(String), widths, aligns, sizes, width),
    qr: (data, size) => {
      safe('setQrCodeSize', size);
      return safe('printQrCode', data, 1);
    },
    bitmap: (source) => safe('printSingleBitmap', source),
    feed: (dots) => safe('printAndFeedPaper', dots),
    lineFeed: () => safe('printAndLineFeed'),
    cut: () => safe('partialCut') || safe('fullCut'),
    openDrawer: () => safe('openCashBox'),
    test: () => safe('printText', 'HostPoint test print\n\n\n'),
    status: async () => {
      if (typeof sdk.getPrinterStatus !== 'function') return -1;
      const type = sdkType(preferredConnectType());
      try {
        // v1.4 of the SDK returns a promise; older builds take a callback
        const result = sdk.getPrinterStatus(type, (cb) => cb);
        const resolved = await Promise.race([
          Promise.resolve(result),
          new Promise((r) => setTimeout(() => r(null), 3000)),
        ]);
        const value = resolved?.value ?? resolved;
        return value === undefined || value === null ? -1 : Number(value);
      } catch {
        return -1;
      }
    },
    info: () => ({ sdk: 'javascript', model: detectIminModel().name }),
  };
};

export const getBackend = () => bridgeBackend() || jsSdkBackend();

// ─── Loading iMin's official JS SDK (vendored, MIT) ───────────────────
// The SDK is a WebSocket client for the printer service that runs on the device
// itself (ws://127.0.0.1:8081/websocket). It only connects on real iMin hardware;
// on SDK V1 devices the shop must also install "iMinprinterplugin" from the iMin
// App Store. Everywhere else connect() resolves false and we stay on the browser.
const SDK_SRC = '/vendor/imin-printer.js';
let sdkAttempt = null;

const loadSdkScript = () =>
  new Promise((resolve) => {
    if (win().IminPrinter) return resolve(true);
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.onload = () => resolve(Boolean(win().IminPrinter));
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

/** Create window.IminPrintInstance by connecting to the device's printer service. */
export const connectJsSdk = async () => {
  if (win().IminPrintInstance) return true;
  if (sdkAttempt) return sdkAttempt;

  sdkAttempt = (async () => {
    if (!(await loadSdkScript())) return false;
    try {
      const IminPrinterCtor = win().IminPrinter;
      const printer = new IminPrinterCtor();
      if (await printer.connect()) {
        win().IminPrintInstance = printer;
        return true;
      }
    } catch (error) {
      console.warn('[iMin] JS SDK connect failed', error);
    }
    return false;
  })();

  const connected = await sdkAttempt;
  if (!connected) sdkAttempt = null; // allow a retry after the plugin is installed
  return connected;
};

/**
 * Full detection: the wrapper bridge answers instantly, the JS SDK needs a
 * WebSocket handshake, so this is async. Returns the transport that answered.
 */
export const detectPrinter = async () => {
  if (bridgeBackend()) return 'bridge';
  if (await connectJsSdk()) return 'jssdk';
  return 'none';
};

/** True only when a real printer transport is reachable — never on user agent alone. */
export const isIminDevice = () => Boolean(getBackend());

/** Weaker check: the hardware looks like an iMin even if no printer SDK answered. */
export const isIminHardware = () => {
  const ua = (navigator?.userAgent || '').toLowerCase();
  return isIminDevice() || ua.includes('imin') || Boolean(detectIminModel().name);
};

const STATUS_TEXT = {
  '-1': 'Not connected',
  0: 'Ready',
  1: 'Not connected or powered off',
  3: 'Print head open',
  7: 'Out of paper',
  8: 'Paper running low',
  99: 'Printer error',
};

export const getPrinterStatus = async () => {
  const backend = getBackend();
  if (!backend) return { status: -1, message: 'No iMin printer found', backend: 'none' };
  const status = await backend.status();
  return {
    status,
    message: STATUS_TEXT[String(status)] || 'Unknown',
    backend: backend.kind,
  };
};

export const getBackendInfo = () => {
  const backend = getBackend();
  const model = detectIminModel();
  return {
    available: Boolean(backend),
    backend: backend?.kind || 'none',
    connectType: preferredConnectType() === CONNECT.SPI ? 'SPI' : 'USB',
    model: model.name || 'Unknown',
    paperWidth: model.paperWidth,
    device: backend?.info?.() || {},
  };
};

let initialised = false;

/** Init once per session; retries with the other connection type before giving up. */
export const initPrinter = async (force = false) => {
  const backend = getBackend();
  if (!backend) return { success: false, method: 'browser' };
  if (initialised && !force) return { success: true, method: backend.kind };

  const first = preferredConnectType();
  const second = first === CONNECT.SPI ? CONNECT.USB : CONNECT.SPI;

  for (const connectType of [first, second]) {
    if (backend.init(connectType) !== false) {
      rememberConnectType(connectType);
      initialised = true;
      return { success: true, method: backend.kind, connectType };
    }
  }
  return { success: false, method: 'browser' };
};

export const printTest = async () => {
  const backend = getBackend();
  if (!backend) throw new Error('No iMin printer found on this device');
  await initPrinter();
  backend.test();
  backend.feed(60);
  backend.cut();
  return { success: true, method: backend.kind };
};

export const openCashDrawer = async () => {
  const backend = getBackend();
  if (!backend) return { success: false, error: 'Cash drawer needs the built-in iMin printer' };
  await initPrinter();
  const ok = backend.openDrawer();
  return ok === false ? { success: false, error: 'The drawer did not respond' } : { success: true };
};

const DIVIDER = '--------------------------------\n';

/** Plain text block printing — used by X/Z shift reports. */
export const printTextBlock = async (lines, { title = '', cut = true } = {}) => {
  const backend = getBackend();
  if (!backend) throw new Error('No iMin printer found on this device');
  await initPrinter();

  if (title) {
    backend.setAlignment(1);
    backend.setTextSize(30);
    backend.setTextStyle(1);
    backend.text(`${title}\n`);
    backend.setTextStyle(0);
  }
  backend.setAlignment(0);
  backend.setTextSize(24);
  backend.text(DIVIDER);
  (Array.isArray(lines) ? lines : String(lines).split('\n')).forEach((line) => {
    backend.text(`${line}\n`);
  });
  backend.text(DIVIDER);
  backend.feed(40);
  if (cut) backend.cut();
  return { success: true, method: backend.kind };
};

// ─── Receipt ──────────────────────────────────────────────────────────
export const printReceiptImin = async (receiptData, paperSize = '80mm') => {
  const backend = getBackend();
  if (!backend) throw new Error('iMin printer not available');

  await initPrinter();
  const width = paperSize === '58mm' ? 384 : 576;

  if (receiptData.logoUrl) {
    backend.setAlignment(1);
    backend.bitmap(receiptData.logoUrl);
    backend.lineFeed();
  }

  backend.setAlignment(1);
  backend.setTextSize(32);
  backend.setTextStyle(1);
  backend.text(`${receiptData.businessName || 'Receipt'}\n`);
  backend.setTextSize(24);
  backend.setTextStyle(0);
  if (receiptData.businessAddress) backend.text(`${receiptData.businessAddress}\n`);
  if (receiptData.businessPhone) backend.text(`${receiptData.businessPhone}\n`);
  if (receiptData.kraPin) backend.text(`PIN: ${receiptData.kraPin}\n`);

  if (receiptData.headerItems?.length) {
    backend.setTextSize(22);
    receiptData.headerItems.forEach((item) => {
      backend.text(item.type === 'text' ? `${item.text}\n` : `${item.key}: ${item.value}\n`);
    });
  }

  backend.text(DIVIDER);

  if (receiptData.documentTitle) {
    backend.setTextStyle(1);
    backend.setAlignment(1);
    backend.text(`${receiptData.documentTitle}\n`);
    backend.setTextStyle(0);
  }

  backend.setAlignment(0);
  backend.setTextSize(24);
  backend.text(`Invoice: ${receiptData.invoiceNumber || ''}\n`);
  if (receiptData.originalInvoiceNumber) backend.text(`Original Invoice: ${receiptData.originalInvoiceNumber}\n`);
  if (receiptData.creditNoteReason) backend.text(`Reason: ${receiptData.creditNoteReason}\n`);
  backend.text(`Date: ${receiptData.date || ''} ${receiptData.time || ''}\n`);
  if (receiptData.serverName) backend.text(`Served By: ${receiptData.serverName}\n`);
  if (receiptData.tableName) backend.text(`Table: ${receiptData.tableName}\n`);
  if (receiptData.customerName) backend.text(`Customer: ${receiptData.customerName}\n`);

  backend.text(DIVIDER);
  backend.columns(['Qty', 'Item', 'Amount'], [1, 4, 2], [0, 0, 2], [22, 22, 22], width);
  backend.text(DIVIDER);

  const line = (item) =>
    backend.columns(
      [String(item.quantity), item.name, (Number(item.price) * Number(item.quantity)).toFixed(2)],
      [1, 4, 2],
      [0, 0, 2],
      [22, 22, 22],
      width
    );

  if (receiptData.groupByCategory && receiptData.groupedItems) {
    Object.entries(receiptData.groupedItems).forEach(([category, items]) => {
      backend.setTextStyle(1);
      backend.setAlignment(1);
      backend.text(`-- ${category} --\n`);
      backend.setTextStyle(0);
      backend.setAlignment(0);
      items.forEach(line);
    });
  } else {
    (receiptData.items || []).forEach(line);
  }

  backend.text(DIVIDER);
  backend.setAlignment(0);
  backend.columns(['Subtotal', '', (receiptData.subtotal || 0).toFixed(2)], [2, 2, 2], [0, 0, 2], [24, 24, 24], width);

  if (receiptData.showDetailedTaxes && receiptData.taxBreakdown?.length) {
    receiptData.taxBreakdown.forEach((tax) => {
      backend.columns(
        [`${tax.name} (${tax.rate}%)`, '', Number(tax.amount || 0).toFixed(2)],
        [3, 1, 2],
        [0, 0, 2],
        [22, 22, 22],
        width
      );
    });
  } else {
    backend.columns(['Tax', '', Number(receiptData.tax || 0).toFixed(2)], [2, 2, 2], [0, 0, 2], [24, 24, 24], width);
  }

  if (receiptData.serviceCharge > 0) {
    backend.columns(['Service Charge', '', receiptData.serviceCharge.toFixed(2)], [3, 1, 2], [0, 0, 2], [22, 22, 22], width);
  }
  if (receiptData.discount > 0) {
    backend.columns(['Discount', '', `-${receiptData.discount.toFixed(2)}`], [2, 2, 2], [0, 0, 2], [24, 24, 24], width);
  }

  backend.setTextStyle(1);
  backend.setTextSize(28);
  backend.columns(
    [receiptData.labels?.total || 'TOTAL', '', Number(receiptData.total || 0).toFixed(2)],
    [2, 2, 2],
    [0, 0, 2],
    [28, 28, 28],
    width
  );
  backend.setTextStyle(0);
  backend.setTextSize(24);

  if (receiptData.paymentMethod) backend.text(`Payment: ${String(receiptData.paymentMethod).toUpperCase()}\n`);
  if (receiptData.mpesaReceipt) backend.text(`M-Pesa: ${receiptData.mpesaReceipt}\n`);
  if (receiptData.pointsEarned > 0) backend.text(`Points Earned: +${receiptData.pointsEarned}\n`);

  backend.text(DIVIDER);
  backend.setAlignment(1);

  if (receiptData.footerItems?.length) {
    receiptData.footerItems.forEach((item) => {
      backend.text(item.type === 'text' ? `${item.text}\n` : `${item.key}: ${item.value}\n`);
    });
  }

  if (receiptData.showQrCode && receiptData.qrCodeUrl) {
    backend.lineFeed();
    backend.qr(receiptData.qrCodeUrl, 5);
    backend.setTextSize(20);
    backend.text('Scan for feedback\n');
  }

  if (receiptData.etims?.submitted) {
    backend.lineFeed();
    backend.setAlignment(1);
    backend.text(DIVIDER);
    backend.setTextStyle(1);
    backend.setTextSize(22);
    backend.text(
      receiptData.etims.document_type === 'credit_note' ? 'KRA eTIMS CREDIT NOTE\n' : 'KRA eTIMS VERIFIED\n'
    );
    backend.setTextStyle(0);
    backend.setTextSize(20);
    backend.text(`CU: ${receiptData.etims.sdc_id || ''}\n`);
    backend.text(`Sign: ${receiptData.etims.rcpt_sign || ''}\n`);
    if (receiptData.etims.qr_data) {
      backend.lineFeed();
      backend.qr(receiptData.etims.qr_data, 6);
      backend.setTextSize(18);
      backend.text('Scan to verify receipt\n');
    }
    backend.text(DIVIDER);
  }

  if (receiptData.isReprint) {
    backend.lineFeed();
    backend.setTextStyle(1);
    backend.text('*** REPRINT ***\n');
    backend.setTextStyle(0);
  }

  backend.feed(30);
  backend.cut();

  return { success: true, method: backend.kind };
};

// ─── Browser fallback ─────────────────────────────────────────────────
export const printBrowser = (htmlContent) => {
  const printWindow = window.open('', '_blank', 'width=400,height=600');
  if (!printWindow) throw new Error('Could not open print window. Please allow popups.');

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    setTimeout(() => printWindow.close(), 1000);
  };
  return { success: true, method: 'browser' };
};

export const printReceipt = async (receiptData, config = {}) => {
  const { printerConnection = 'auto', paperSize = '80mm', fallbackToBrowser = true } = config;

  if ((printerConnection === 'auto' || printerConnection === 'builtin') && isIminDevice()) {
    try {
      return await printReceiptImin(receiptData, paperSize);
    } catch (error) {
      console.warn('iMin print failed, falling back to browser:', error);
      if (!fallbackToBrowser) throw error;
    }
  }

  if (fallbackToBrowser || printerConnection === 'browser') {
    return printBrowser(generateReceiptHTML(receiptData, paperSize));
  }

  throw new Error('No suitable print method available');
};

const generateReceiptHTML = (data, paperSize) => {
  const width = paperSize === '58mm' ? '48mm' : '72mm';
  const etimsSection = data.etims?.submitted
    ? `
    <div class="divider"></div>
    <div class="center bold" style="font-size:14px;">${data.etims.document_type === 'credit_note' ? 'KRA eTIMS CREDIT NOTE' : 'KRA eTIMS VERIFIED'}</div>
    <div class="center" style="font-size:10px;">CU: ${data.etims.sdc_id || ''}</div>
    <div class="center" style="font-size:10px;">Sign: ${data.etims.rcpt_sign || ''}</div>
    ${data.etims.qr_base64 ? `<div class="center" style="margin:8px 0;"><img src="data:image/png;base64,${data.etims.qr_base64}" style="width:120px;height:120px;" alt="eTIMS QR"/></div>` : ''}
    <div class="center" style="font-size:9px;">Scan to verify at KRA</div>
  `
    : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${data.documentTitle || 'Receipt'}</title>
      <style>
        @media print { @page { margin: 0; size: ${paperSize} auto; } }
        body { font-family: 'Courier New', monospace; font-size: 12px; width: ${width}; margin: 0 auto; padding: 5mm; }
        .center { text-align: center; }
        .bold { font-weight: bold; }
        .divider { border-top: 1px dashed #000; margin: 5px 0; }
        .row { display: flex; justify-content: space-between; }
        .large { font-size: 16px; }
      </style>
    </head>
    <body>
      ${data.logoUrl ? `<div class="center"><img src="${data.logoUrl}" style="max-width:100px;max-height:40px"/></div>` : ''}
      <div class="center bold large">${data.businessName || 'Receipt'}</div>
      ${data.businessAddress ? `<div class="center">${data.businessAddress}</div>` : ''}
      <div class="divider"></div>
      ${data.documentTitle ? `<div class="center bold large">${data.documentTitle}</div>` : ''}
      <div>Invoice: ${data.invoiceNumber || ''}</div>
      ${data.originalInvoiceNumber ? `<div>Original Invoice: ${data.originalInvoiceNumber}</div>` : ''}
      ${data.creditNoteReason ? `<div>Reason: ${data.creditNoteReason}</div>` : ''}
      <div>Date: ${data.date || ''} ${data.time || ''}</div>
      ${data.serverName ? `<div>Served By: ${data.serverName}</div>` : ''}
      ${data.tableName ? `<div>Table: ${data.tableName}</div>` : ''}
      <div class="divider"></div>
      ${(data.items || [])
        .map(
          (item) => `
        <div class="row">
          <span>${item.quantity}x ${item.name}</span>
          <span>${(Number(item.price) * Number(item.quantity)).toFixed(2)}</span>
        </div>`
        )
        .join('')}
      <div class="divider"></div>
      <div class="row"><span>Subtotal</span><span>${(data.subtotal || 0).toFixed(2)}</span></div>
      <div class="row"><span>Tax</span><span>${(data.tax || 0).toFixed(2)}</span></div>
      <div class="row bold large"><span>${data.labels?.total || 'TOTAL'}</span><span>${(data.total || 0).toFixed(2)}</span></div>
      ${etimsSection}
      <div class="divider"></div>
      ${data.footerItems?.map((item) => `<div class="center">${item.type === 'text' ? item.text : `${item.key}: ${item.value}`}</div>`).join('') || ''}
      ${data.isReprint ? '<div class="center bold">*** REPRINT ***</div>' : ''}
    </body>
    </html>
  `;
};

export default {
  isIminDevice,
  isIminHardware,
  detectIminModel,
  getBackend,
  getBackendInfo,
  initPrinter,
  getPrinterStatus,
  printReceipt,
  printReceiptImin,
  printTextBlock,
  printTest,
  printBrowser,
  openCashDrawer,
};
